import lendersJson from "@/data/lenders.json";

export type PseoLender = {
  id: string;
  name: string;
  minAmount: number;
  maxAmount: number;
  minInterest: number;
  effRate?: string;
  allowsUC: boolean;
  allowsBadCredit: boolean;
  affiliateUrl: string;
  rating: number;
  description: string;
};

export const PSEO_LENDERS = lendersJson as PseoLender[];

/** Alla beloppssteg vi bygger pSEO-sidor för: 5 000 kr – 600 000 kr. */
export function amountSteps(): number[] {
  const steps: number[] = [];
  for (let a = 5000; a < 50000; a += 5000) steps.push(a);
  for (let a = 50000; a < 100000; a += 10000) steps.push(a);
  for (let a = 100000; a < 300000; a += 25000) steps.push(a);
  for (let a = 300000; a <= 600000; a += 50000) steps.push(a);
  return steps;
}

export const AMOUNT_SLUGS = amountSteps().map((a) => `${a}-kr`);

export function parseAmountSlug(slug: string): number | null {
  const m = /^(\d+)-kr$/.exec(slug);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < 1000 || n > 2000000) return null;
  return n;
}

export function formatKr(n: number) {
  return new Intl.NumberFormat("sv-SE").format(Math.round(n)) + " kr";
}

export function lendersForAmount(amount: number) {
  return PSEO_LENDERS.filter((l) => l.minAmount <= amount && l.maxAmount >= amount).sort(
    (a, b) => b.rating - a.rating || a.minInterest - b.minInterest,
  );
}

export type CategoryDef = {
  slug: string;
  h1: string;
  title: string;
  description: string;
  intro: string;
  defaultAmount: number;
  filter: (l: PseoLender) => boolean;
};

export const CATEGORIES: CategoryDef[] = [
  {
    slug: "lan-utan-uc",
    h1: "Lån utan UC – jämför långivare",
    title: "Lån utan UC – jämför långivare utan kreditupplysning hos UC",
    description:
      "Jämför lån utan UC 2026. Se långivare som använder andra kreditupplysningsbolag, räntor från 4,5 % och ansök direkt online.",
    intro:
      "Här jämför du långivare som tar kreditupplysning via andra bolag än UC. Det kan vara ett alternativ om du vill undvika en UC-förfrågan i din kreditfil. Kom ihåg att alla seriösa långivare alltid gör en kreditprövning.",
    defaultAmount: 40000,
    filter: (l) => l.allowsUC === false,
  },
  {
    slug: "samla-lan",
    h1: "Samla lån – jämför samlingslån och sänk räntan",
    title: "Samla lån 2026 – jämför samlingslån och sänk månadskostnaden",
    description:
      "Samla dyra krediter och smslån till ett lån med lägre ränta. Jämför långivare för samlingslån upp till 800 000 kr – en ansökan, flera bud.",
    intro:
      "Med ett samlingslån ersätter du flera dyra krediter med ett enda lån till lägre ränta. Nedan jämför du långivare och förmedlare som hanterar samlingslån på större belopp.",
    defaultAmount: 150000,
    filter: (l) => l.maxAmount >= 100000,
  },
  {
    slug: "lan-med-betalningsanmarkning",
    h1: "Lån med betalningsanmärkning",
    title: "Lån med betalningsanmärkning – jämför långivare 2026",
    description:
      "Jämför långivare som kan bevilja lån med betalningsanmärkning. Se villkor, räntor och krav – och ansök tryggt online.",
    intro:
      "Alla långivare säger inte nej vid en betalningsanmärkning. Nedan listar vi aktörer som gör en helhetsbedömning av din ekonomi i stället för att avslå direkt.",
    defaultAmount: 40000,
    filter: (l) => l.allowsBadCredit === true,
  },
  {
    slug: "snabba-lan",
    h1: "Snabba lån med snabb utbetalning",
    title: "Snabba lån – jämför långivare med snabb utbetalning",
    description:
      "Jämför snabba lån med utbetalning samma dag. Se räntor, belopp och villkor från våra granskade långivare.",
    intro:
      "Behöver du pengarna snabbt? Nedan jämför du långivare med digital ansökan och snabb handläggning – flera betalar ut samma bankdag.",
    defaultAmount: 25000,
    filter: (l) => l.minAmount <= 25000,
  },
];

export function getCategory(slug: string) {
  return CATEGORIES.find((c) => c.slug === slug) ?? null;
}

/** Annuitetsberäkning av månadskostnad. */
export function monthlyCost(amount: number, years: number, annualRate: number) {
  const n = years * 12;
  const r = annualRate / 100 / 12;
  if (r === 0) return amount / n;
  return (amount * r) / (1 - Math.pow(1 + r, -n));
}
